import { useEffect, useRef, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';

const NAV_LINKS = [
  { to: '/courses', label: 'Courses' },
  { to: '/aboutMe', label: 'About me' },
  { to: '/contactMe', label: 'Contact me' },
] as const;

export default function Navbar(): JSX.Element {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navHidden, setNavHidden] = useState(false);
  const [navHeight, setNavHeight] = useState(0);
  const navRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);
  const lastWidth = useRef(0);

  function handleLogout(): void {
    signOut(auth).catch((signOutError: unknown): void => {
      console.error('Sign-out failed', signOutError);
    });
  }

  // Hide on scroll down, show on scroll up. Never hide while the mobile menu is open.
  useEffect(() => {
    function handleScroll(): void {
      if (menuOpen) return;
      const currentScrollY = window.scrollY;
      const scrolledPastNav = currentScrollY > navHeight;
      const scrollingDown = currentScrollY > lastScrollY.current;
      setNavHidden(scrollingDown && scrolledPastNav);
      lastScrollY.current = currentScrollY;
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return (): void => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [menuOpen, navHeight]);

  // Close the mobile menu on real resizes (width change), not on mobile browser
  // chrome showing/hiding, which only changes height.
  useEffect(() => {
    lastWidth.current = window.innerWidth;
    function handleResize(): void {
      if (window.innerWidth !== lastWidth.current) {
        lastWidth.current = window.innerWidth;
        setMenuOpen(false);
      }
    }
    window.addEventListener('resize', handleResize);
    return (): void => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Measure the nav block's real rendered height (h-20 alone, or h-20 + open menu)
  // so the spacer below can push page content down by exactly that much.
  useEffect(() => {
    if (navRef.current === null) return;
    setNavHeight(navRef.current.getBoundingClientRect().height);
  }, [menuOpen]);

  function closeMenu(): void {
    setMenuOpen(false);
  }

  return (
    <>
      <div
        ref={navRef}
        className={`fixed inset-x-0 top-0 z-50 bg-white shadow-sm transition-transform duration-300 ${
          navHidden ? '-translate-y-full' : 'translate-y-0'
        }`}
      >
        <div className="flex h-20 items-center justify-between px-4 md:px-8">
          <Link to="/" className="flex items-center">
            <img src="/logo.webp" alt="Shaping Colours" width={500} height={201} className="h-20 w-auto" />
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            {NAV_LINKS.map((link) => (
              <Link key={link.to} to={link.to} className="text-neutral-800 hover:text-neutral-500">
                {link.label}
              </Link>
            ))}
            {user !== null ? (
              <>
                <Link to="/admin/upload" className="text-neutral-800 hover:text-neutral-500">
                  Upload Images
                </Link>
                <Link to="/admin/backup" className="text-neutral-800 hover:text-neutral-500">
                  Download Backup
                </Link>
                <button type="button" onClick={handleLogout} className="text-neutral-800 hover:text-neutral-500">
                  Logout
                </button>
              </>
            ) : null}
          </nav>

          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center md:hidden"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => {
              setMenuOpen((open) => !open);
            }}
          >
            {menuOpen ? (
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            )}
          </button>
        </div>

        {menuOpen ? (
          <nav className="flex flex-col gap-1 border-t border-neutral-200 px-4 py-4 md:hidden">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={closeMenu}
                className="rounded px-2 py-3 text-neutral-800 hover:bg-neutral-100"
              >
                {link.label}
              </Link>
            ))}
            {user !== null ? (
              <>
                <Link
                  to="/admin/upload"
                  onClick={closeMenu}
                  className="rounded px-2 py-3 text-neutral-800 hover:bg-neutral-100"
                >
                  Upload Images
                </Link>
                <Link
                  to="/admin/backup"
                  onClick={closeMenu}
                  className="rounded px-2 py-3 text-neutral-800 hover:bg-neutral-100"
                >
                  Download Backup
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    closeMenu();
                    handleLogout();
                  }}
                  className="rounded px-2 py-3 text-left text-neutral-800 hover:bg-neutral-100"
                >
                  Logout
                </button>
              </>
            ) : null}
          </nav>
        ) : null}
      </div>

      {/* Sibling spacer, not the fixed nav itself — grows with navHeight so page
          content is pushed down instead of hidden underneath the fixed nav. */}
      <div style={{ paddingTop: navHeight }} />
    </>
  );
}
